/**
 * Type definitions for Data Upload functionality
 * Provides comprehensive type safety for raw data upload workflows
 */

import { UploadProgress as StandardUploadProgress } from '@/shared/components/feedback/ProgressTracker';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Default timezone for data processing */
export const DEFAULT_TIMEZONE = "Europe/Vienna";

/** Default header option (headers present) */
export const DEFAULT_HEADER_OPTION = "ja";

/** Delay for scroll animation in milliseconds */
export const SCROLL_DELAY_MS = 100;

/** Chunk size for streaming file processing (1MB) */
export const CHUNK_SIZE = 1024 * 1024;

// =============================================================================
// WORKFLOW CONFIGURATION
// =============================================================================

/** Workflow steps for the data upload flow */
export const WORKFLOW_STEPS = [
  { id: 1, label: 'workflow.steps.upload', description: 'workflow.steps.uploadDesc' },
  { id: 2, label: 'workflow.steps.metadata', description: 'workflow.steps.metadataDesc' },
  { id: 3, label: 'workflow.steps.dataSelection', description: 'workflow.steps.dataSelectionDesc' },
  { id: 4, label: 'workflow.steps.process', description: 'workflow.steps.processDesc' },
  { id: 5, label: 'workflow.steps.save', description: 'workflow.steps.saveDesc' }
];

// =============================================================================
// CORE TYPES
// =============================================================================

/** Column validation error state */
export interface ColumnValidationErrors {
  column1: boolean;
  column2: boolean;
  column3: boolean;
}

/** Default column validation state */
export const DEFAULT_COLUMN_VALIDATION: ColumnValidationErrors = {
  column1: false,
  column2: false,
  column3: false
};

/** File validation data for multi-file validation */
export interface FileValidationData {
  file: File;
  content: string;
  detectedDelimiter: string;
  headers: string[];
  columnCount: number;
}

/** Parameters for upload operation */
export interface UploadParameters {
  timezone: string;
  dropdownCount: '2' | '3';
  valueColumnName: string;
  selectedColumns: {
    column1: string;
    column2: string;
    column3?: string;
  };
  customDateFormat?: string;
}

/** Info file generation parameters */
export interface InfoFileParams {
  originalFileNames: string[];
  delimiter: string;
  radioHasHeader: string;
  selectedTimezone: string;
  hasSeparateDateAndTime: boolean;
  selectedColumn1: string;
  selectedColumn2: string;
  selectedColumn3: string;
  valueColumnName: string;
  customDateFormat: string;
  t: (key: string, options?: { ns?: string; [key: string]: unknown }) => string;
}

// =============================================================================
// UI STATE MANAGEMENT
// =============================================================================

/** UI State for the main component */
export interface UploadUIState {
  currentStep: number;
  completedSteps: number[];
  isLoading: boolean;
  loadingMessage: string | null;
  isNameModalOpen: boolean;
  saveDataName: string;
  errorMessage: string | null;
  errorCode: string | null;
  errorSuggestion: string | null;
  errorPersistent: boolean;
  successMessage: string | null;
  showFileUpload: boolean;
  showActionButtons: boolean;
  showDelimiterInput: boolean;
  shouldScrollToBottom: boolean;
  columnValidationErrors: ColumnValidationErrors;
  showUpgradeModal: boolean;
  upgradeMessage: string;
  upgradeResourceType: 'upload' | 'processing' | 'storage';
}

/** Action types for UI reducer */
export type UploadUIAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'MARK_STEP_COMPLETED'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_LOADING_MESSAGE'; payload: string | null }
  | { type: 'TOGGLE_MODAL'; payload: boolean }
  | { type: 'SET_SAVE_NAME'; payload: string }
  | { type: 'SET_ERROR'; payload: string | null | { message: string; code?: string; suggestion?: string; persistent?: boolean } }
  | { type: 'SET_SUCCESS'; payload: string | null }
  | { type: 'SHOW_FILE_UPLOAD'; payload: boolean }
  | { type: 'SHOW_ACTION_BUTTONS'; payload: boolean }
  | { type: 'SHOW_DELIMITER_INPUT'; payload: boolean }
  | { type: 'SET_SCROLL_BOTTOM'; payload: boolean }
  | { type: 'SET_COLUMN_ERRORS'; payload: ColumnValidationErrors }
  | { type: 'CLEAR_COLUMN_ERRORS' }
  | { type: 'SHOW_UPGRADE_MODAL'; payload: boolean }
  | { type: 'SET_UPGRADE_INFO'; payload: { message: string; resourceType: 'upload' | 'processing' | 'storage' } }
  | { type: 'START_PROCESSING' }
  | { type: 'STOP_PROCESSING' }
  | { type: 'RESET_UI' };

// =============================================================================
// UPLOAD PROGRESS
// =============================================================================

/** Upload progress type alias for data upload */
export type DataUploadProgress = StandardUploadProgress;
